import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { useLexicon } from '../../hooks/useLexicon';

import {
  loadPlaylistsIndex,
  removePlaylist,
  renamePlaylist,
  FAVORITES_TITLE,
} from '../../store/player_favorites/favoritesService';
import {
  selectAllPlaylists,
  selectPlaylistsLoading,
} from '../../store/player_favorites/favoritesSlice';
import { createVideoList } from './api';
import PlayerLoader, { InlinePlayerLoader } from '../../components/PlayerLoader';
import play from '../../images/ic_play-button.svg';
import useShare from '../../hooks/useShare';

const eventIdOf = (v) => v?.event_id ?? v?.eventId ?? v?.details?.event_id ?? null;

// Sum (end_second - start_second) across the playlist's videos. Returns 0 when
// the playlist is empty or the rows are missing timing fields.
const totalDurationSeconds = (videos) => {
  if (!Array.isArray(videos) || videos.length === 0) return 0;
  return videos.reduce((acc, v) => {
    const s = Number(v?.start_second);
    const e = Number(v?.end_second);
    if (!Number.isFinite(s) || !Number.isFinite(e)) return acc;
    return acc + Math.max(0, e - s);
  }, 0);
};

const formatDurationLabel = (seconds) => {
  if (!seconds || seconds <= 0) return null;
  const total = Math.round(seconds);
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  if (mm === 0) return `${ss}s`;
  if (ss === 0) return `${mm}m`;
  return `${mm}m ${ss}s`;
};

// /player/favorites — list of the player's saved playlists. The permanent
// Favorites entry (built up via the star toggle on event rows) is pinned at
// the top. Other entries come from "Save playlist" on /player/events/video.
const PlayerFavorites = () => {
  const l = useLexicon();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const playlists = useSelector(selectAllPlaylists);
  const loading = useSelector(selectPlaylistsLoading);
  const [openingId, setOpeningId] = useState(null);
  const [sharingId, setSharingId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const { share, status: shareStatus } = useShare();
  const [shareToastForId, setShareToastForId] = useState(null);

  useEffect(() => {
    dispatch(loadPlaylistsIndex());
  }, [dispatch]);

  // Clear the per-row toast indicator when useShare's status flips back to null
  // (after its 2s autohide). Keeps the toast scoped to the row the user tapped.
  useEffect(() => {
    if (shareStatus === null) setShareToastForId(null);
  }, [shareStatus]);

  const openPlaylist = async (playlist) => {
    const markerIds = (playlist.videos || [])
      .map(eventIdOf)
      .filter((id) => id != null)
      .map(Number);
    if (markerIds.length === 0) return;
    setOpeningId(playlist.id);
    try {
      // Mint a fresh watch token and reuse the existing EventsVideo viewer.
      // Cheaper than building a separate "play saved playlist" route and
      // matches the user's existing watch flow.
      const res = await createVideoList(markerIds, playlist.title || 'Favorites');
      const tk = res.data?.token;
      if (tk) navigate(`/player/events/video?token=${tk}`);
    } finally {
      setOpeningId(null);
    }
  };

  // Share creates a fresh public token for this playlist and copies the
  // /shared/playlist/:token URL to the clipboard. Same public viewer the
  // EventsVideo ShareButton uses (SMRTSTATS-286 #4).
  const onShare = async (playlist, e) => {
    e.stopPropagation();
    e.preventDefault();
    const markerIds = (playlist.videos || [])
      .map(eventIdOf)
      .filter((id) => id != null)
      .map(Number);
    if (markerIds.length === 0) return;
    setSharingId(playlist.id);
    try {
      const res = await createVideoList(markerIds, playlist.title || 'Favorites');
      const token = res.data?.token;
      if (!token) return;
      const url = `${window.location.origin}/shared/playlist/${token}`;
      setShareToastForId(playlist.id);
      await share({ url, title: playlist.title, text: playlist.title });
    } finally {
      setSharingId(null);
    }
  };

  const startRename = (playlist, e) => {
    e.stopPropagation();
    e.preventDefault();
    setRenamingId(playlist.id);
    setRenameDraft(playlist.title || '');
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft('');
  };

  const submitRename = async (playlist) => {
    const next = renameDraft.trim();
    if (!next || next === playlist.title) {
      cancelRename();
      return;
    }
    await dispatch(renamePlaylist({ playlistId: playlist.id, title: next }));
    cancelRename();
  };

  return (
    <Wrap>
      <Title>{l(1851)}</Title>

      {loading && playlists.length === 0 && (
        <PlayerLoader label={l(1862)} />
      )}

      {!loading && playlists.length === 0 && (
        <Empty>
          <EmptyTitle>{l(1863)}</EmptyTitle>
          <EmptyHint>{l(1864)}</EmptyHint>
          <EmptyCta to="/player/events">{l(1865)}</EmptyCta>
        </Empty>
      )}

      {playlists.length > 0 && (
        <List>
          {playlists.map((p) => {
            const count = (p.videos || []).length;
            const isFav = p.title === FAVORITES_TITLE;
            const isEmpty = count === 0;
            const durationLabel = formatDurationLabel(totalDurationSeconds(p.videos));
            const isRenaming = renamingId === p.id;
            const onRemove = (e) => {
              e.stopPropagation();
              e.preventDefault();
              const ok = window.confirm(l(1877).replace('{x}', `"${p.title}"`));
              if (ok) dispatch(removePlaylist({ playlistId: p.id }));
            };
            return (
              <RowOuter key={p.id} $pinned={isFav}>
                <RowMain
                  type="button"
                  onClick={() => !isRenaming && openPlaylist(p)}
                  disabled={openingId !== null || isEmpty || isRenaming}
                  aria-label={`${l(1870)} ${p.title}`}
                >
                  <Body>
                    <NameRow>
                      {isFav && <Pin>★</Pin>}
                      {isRenaming ? (
                        <RenameInput
                          autoFocus
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              submitRename(p);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelRename();
                            }
                          }}
                          aria-label={l(1866)}
                        />
                      ) : (
                        <Name>{p.title}</Name>
                      )}
                    </NameRow>
                    <Meta>
                      {count} {count === 1 ? l(1867) : l(1868)}
                      {durationLabel && ` · ${durationLabel}`}
                      {isFav && ` · ${l(1869)}`}
                    </Meta>
                  </Body>
                  {openingId === p.id ? (
                    <InlinePlayerLoader />
                  ) : (
                    <PlayIcon src={play} alt="" $dim={isEmpty} />
                  )}
                </RowMain>

                {/* Action rail — Share / Rename / Remove. Rename + Remove are
                    hidden on the permanent Favorites row (un-renameable by
                    title-uniqueness, un-removable by spec). */}
                <Actions>
                  {!isRenaming && (
                    <>
                      <ActionBtn
                        type="button"
                        onClick={(e) => onShare(p, e)}
                        disabled={isEmpty || sharingId === p.id}
                        aria-label={`${l(1861)} ${p.title}`}
                      >
                        {sharingId === p.id ? <InlinePlayerLoader /> : l(1861)}
                      </ActionBtn>
                      {!isFav && (
                        <ActionBtn
                          type="button"
                          onClick={(e) => startRename(p, e)}
                          aria-label={`${l(971)} ${p.title}`}
                        >
                          {l(971)}
                        </ActionBtn>
                      )}
                      {!isFav && (
                        <RemoveBtn
                          type="button"
                          onClick={onRemove}
                          aria-label={`${l(1871)} ${p.title}`}
                        >
                          ×
                        </RemoveBtn>
                      )}
                    </>
                  )}
                  {isRenaming && (
                    <>
                      <ActionBtn
                        type="button"
                        onClick={(e) => { e.stopPropagation(); submitRename(p); }}
                        aria-label={l(1872)}
                      >
                        {l(30)}
                      </ActionBtn>
                      <ActionBtn
                        type="button"
                        $muted
                        onClick={(e) => { e.stopPropagation(); cancelRename(); }}
                        aria-label={l(1873)}
                      >
                        {l(1709)}
                      </ActionBtn>
                    </>
                  )}
                </Actions>

                {shareToastForId === p.id && shareStatus === 'shared' && (
                  <ShareToast role="status">{l(1874)}</ShareToast>
                )}
                {shareToastForId === p.id && shareStatus === 'copied' && (
                  <ShareToast role="status">{l(1875)}</ShareToast>
                )}
                {shareToastForId === p.id && shareStatus === 'failed' && (
                  <ShareToast $error role="status">{l(1876)}</ShareToast>
                )}
              </RowOuter>
            );
          })}
        </List>
      )}
    </Wrap>
  );
};

export default PlayerFavorites;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h1`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 22px;
  margin: 0;
  color: ${(props) => props.theme.white};
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 24px;
  text-align: center;
  border: 1px dashed ${(props) => props.theme.borderColor};
  border-radius: 16px;
`;

const EmptyTitle = styled.div`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 16px;
  color: ${(props) => props.theme.white};
`;

const EmptyHint = styled.p`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  color: ${(props) => props.theme.blueish2};
  margin: 0;
  line-height: 1.4;
  max-width: 280px;
`;

const EmptyCta = styled(Link)`
  margin-top: 4px;
  padding: 10px 18px;
  border-radius: 999px;
  background: ${(props) => props.theme.green};
  color: ${(props) => props.theme.black};
  text-decoration: none;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 13px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RowOuter = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => (props.$pinned ? props.theme.green : props.theme.borderColor)};
  border-radius: 12px;
  padding: 14px 16px;
`;

const RowMain = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-align: left;
  color: inherit;
  font: inherit;

  &:disabled { opacity: 0.6; cursor: default; }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const NameRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Pin = styled.span`
  color: ${(props) => props.theme.green};
  font-size: 14px;
`;

const Name = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 15px;
  color: ${(props) => props.theme.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RenameInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.green};
  }
`;

const Meta = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(props) => props.theme.blueish2};
`;

const PlayIcon = styled.img`
  width: 14px;
  height: 14px;
  filter: brightness(0) invert(1);
  opacity: ${(props) => (props.$dim ? 0.3 : 0.8)};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${(props) => props.theme.borderColor};
`;

const ActionBtn = styled.button`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background: transparent;
  color: ${(props) => (props.$muted ? props.theme.blueish2 : props.theme.white)};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: border-color 120ms ease, color 120ms ease;

  &:hover:not(:disabled) {
    border-color: ${(props) => props.theme.blueish2};
    color: ${(props) => props.theme.white};
  }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const RemoveBtn = styled.button`
  width: 28px;
  height: 28px;
  margin-left: auto;
  flex-shrink: 0;
  border-radius: 999px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background: transparent;
  color: ${(props) => props.theme.blueish2};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: color 120ms ease, border-color 120ms ease;

  &:hover {
    color: ${(props) => props.theme.white};
    border-color: ${(props) => props.theme.white};
  }
`;

const ShareToast = styled.span`
  position: absolute;
  right: 16px;
  bottom: -22px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${(props) => (props.$error ? '#3a1414' : props.theme.green)};
  color: ${(props) => (props.$error ? props.theme.white : props.theme.black)};
  font-family: 'SatoshiBold', sans-serif;
  font-size: 11px;
  letter-spacing: 0.3px;
  white-space: nowrap;
  pointer-events: none;
`;
